import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UserSessionService } from '../services/mock-auth.service';
import { environment } from '../../../environments/environment';

/**
 * Pre-Registration Guard
 * 
 * Previene l'accesso alla pagina di pre-registration se l'utente
 * ha già completato il processo e ha un professional_id valido.
 * 
 * LOGICA:
 * - Se l'utente HA già un professional_id:
 *   → Professional/Collaborator: Redirect a /axa/professionals-registration-progress
 *   → Backoffice: Redirect a /axa/dashboard
 * - Se l'utente NON ha un professional_id → Permette accesso a pre-registration
 * 
 * IMPORTANTE:
 * - Questo controllo si applica principalmente a utenti con ruolo 'professional'
 * - Gli admin e backoffice possono sempre accedere (per gestione)
 * 
 * Usage:
 * ```typescript
 * {
 *   path: 'axa/pre-registration',
 *   component: PreRegistrationComponent,
 *   canActivate: [AuthGuard, PreRegistrationGuard],
 *   data: { roles: ['professional', ...] }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class PreRegistrationGuard implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);
  private mockAuthService = inject(UserSessionService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Skip in development if guards are disabled
    const disableGuards = (environment as any).disableAuthGuards;
    if (disableGuards && !environment.production) {
      console.log('🔓 PreRegistrationGuard bypassed (development only)');
      return true;
    }

    // Get current user from JWT token
    const user = this.authService.getCurrentUser();
    
    if (!user) {
      console.log('❌ PreRegistrationGuard: No user (invalid token)');
      return this.router.createUrlTree(['/login']);
    }

    const userRoles = user.roles || [];
    
    // Admin and backoffice users can always access pre-registration page (for management purposes)
    const isBackoffice = userRoles.some(role => 
      role === 'admin' || 
      role === 'backoffice_admin' || 
      role === 'backoffice_professional' || 
      role === 'backoffice_supplier'
    );
    
    if (isBackoffice) {
      console.log('✅ PreRegistrationGuard: Backoffice user, allowing access to pre-registration');
      return true;
    }
    
    // Check if professional already exists
    // First check JWT token (most secure)
    let professionalId: string | null = user.professionalId || null;
    
    // Fallback: check sessionStorage or mockAuthService
    if (!professionalId) {
      professionalId = this.mockAuthService.getDemoProfessionalId();
    }
    
    // If professional_id exists, user has already completed pre-registration
    if (professionalId && professionalId.trim()) {
      console.log('⚠️ PreRegistrationGuard: Professional already exists (ID:', professionalId, ')');
      
      // Determine redirect based on role
      const isProfessionalOrCollaborator = userRoles.includes('professional') || 
                                           userRoles.includes('collaborator');
      
      if (isProfessionalOrCollaborator) {
        // Professional/Collaborator → Progression step
        console.log('→ Redirecting to professionals-registration-progress');
        return this.router.createUrlTree(['/axa/professionals-registration-progress']);
      } else {
        // Backoffice → Dashboard
        console.log('→ Redirecting to dashboard');
        return this.router.createUrlTree(['/axa/dashboard']);
      }
    }
    
    // No professional_id found - user can access pre-registration
    console.log('✅ PreRegistrationGuard: No professional_id found, allowing access to pre-registration');
    return true;
  }
}
